import { RoutePaths } from "enums/RoutePaths";
import { useCallback, useEffect, useRef } from "react";
import { Location, useBlocker } from "react-router-dom";
import { ConfirmOptions, useConfirm } from "stories/Admin";

/**
 * `navigate(to, { state: LEAVE_WITHOUT_ASKING })` hopper over spørsmålet om
 * ulagrede endringer. Brukes når brukeren bytter moské eller modus: da er
 * siden uansett ikke lenger den samme.
 */
export const LEAVE_WITHOUT_ASKING = { leaveWithoutAsking: true } as const;

/** Om en navigering fra `current` til `next` skal stoppes og spørres om. */
export const shouldAskBeforeLeaving = (
  dirty: boolean,
  current: Pick<Location, "pathname">,
  next: Pick<Location, "pathname" | "state">,
) => {
  if (!dirty) return false;
  if (current.pathname === next.pathname) return false;
  // Utlogging og utløpt innlogging: brukeren er allerede logget ut.
  if (next.pathname === RoutePaths.Login) return false;
  const state = next.state as { leaveWithoutAsking?: boolean } | null;
  return !state?.leaveWithoutAsking;
};

/**
 * Spør før brukeren går til en annen side i adminen (meny, lenker,
 * tilbakeknappen i nettleseren) mens `isDirty()` er sann. «Forkast» går
 * videre, ellers blir brukeren på siden. Krever en data router
 * (`createBrowserRouter`).
 */
const useLeaveGuard = (isDirty: () => boolean, options: ConfirmOptions) => {
  const confirm = useConfirm();
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const askingRef = useRef(false);

  const shouldBlock = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: Location;
      nextLocation: Location;
    }) =>
      shouldAskBeforeLeaving(
        isDirtyRef.current(),
        currentLocation,
        nextLocation,
      ),
    [],
  );

  const blocker = useBlocker(shouldBlock);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state !== "blocked" || askingRef.current) return;
    askingRef.current = true;
    confirm(optionsRef.current)
      .then((ok) => {
        const current = blockerRef.current;
        if (current.state !== "blocked") return;
        if (ok) current.proceed();
        else current.reset();
      })
      .finally(() => {
        askingRef.current = false;
      });
  }, [blocker.state, confirm]);
};

export default useLeaveGuard;
